import React, { useEffect, useState, useRef, useCallback } from 'react';
import QRCode from 'qrcode.react';
import Lottie from 'lottie-react';

import Button from '@material-ui/core/Button';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import IconButton from '@material-ui/core/IconButton';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import WifiOffIcon from '@material-ui/icons/WifiOff';

import * as KioskCodeService from '../../api/kioskCodeService';
import * as StampCampaignService from '../../api/stampCampaignService';
import * as LoyaltyProgramService from '../../api/loyaltyProgramService';

import useCompanyContext from '../../hooks/useCompanyContext';
import useNetworkStatus from '../../hooks/useNetworkStatus';
import useResponsiveScreen, {
  getQrSize,
  getFittedQrSize,
  LANDSCAPE_QR_GUTTER,
} from '../../hooks/useResponsiveScreen';
import useMeasuredHeight from '../../hooks/useMeasuredHeight';
import UnlockPINDialog from '../Dialog/UnlockPINDialog';

import cogs from '../../assets/animations/cogs-work.json';
import { version } from '../../../package.json';

const KIOSK_NETWORK_ERROR_EVENT = 'kiosk-network-error';

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: theme.spacing(6),
    [theme.breakpoints.down('xs')]: {
      padding: '0 8px',
    },
  },
  logoContainer: {
    marginTop: 'clamp(24px, 8vmin, 90px)',
    marginBottom: theme.spacing(4),
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'center',
    width: '100vw',
    position: 'relative',
    [theme.breakpoints.down('xs')]: {
      marginBottom: theme.spacing(2),
    },
  },
  // Merchant logo on the portrait screen. Scales with vmin (= width in
  // portrait), clamped so it stays readable on phones and bounded on tablets.
  merchantLogo: {
    height: 'clamp(108px, 34vmin, 240px)',
    width: 'clamp(108px, 34vmin, 240px)',
    borderRadius: '50%',
    objectFit: 'cover',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
  },
  logoFallback: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.grey[300],
    color: theme.palette.grey[700],
    fontWeight: theme.typography.fontWeightMedium,
    textTransform: 'uppercase',
  },
  header: {
    marginTop: theme.spacing(3),
    textAlign: 'center',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    lineHeight: 1.2,
    // Proportional to the screen: scales with vmin (= width in portrait),
    // clamped so it stays readable on phones and bounded on large tablets.
    fontSize: 'clamp(24px, 8vmin, 56px)',
    paddingLeft: theme.spacing(5),
    paddingRight: theme.spacing(5),
    [theme.breakpoints.down('xs')]: {
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(3),
    },
  },
  countdownText: {
    marginTop: theme.spacing(2),
    fontWeight: theme.typography.fontWeightRegular,
    fontSize: 'clamp(14px, 2.9vmin, 22px)',
    textAlign: 'center',
    [theme.breakpoints.down('xs')]: {
      marginTop: theme.spacing(),
    },
  },
  countdownTextHidden: {
    marginTop: theme.spacing(2),
    fontWeight: theme.typography.fontWeightRegular,
    fontSize: 'clamp(14px, 2.9vmin, 22px)',
    textAlign: 'center',
    color: 'transparent',
    [theme.breakpoints.down('xs')]: {
      marginTop: theme.spacing(),
    },
  },
  countdownTextLandscape: {
    marginTop: theme.spacing(),
    fontWeight: theme.typography.fontWeightRegular,
    fontSize: 'clamp(17px, 3.1vmin, 29px)',
    textAlign: 'center',
    marginBottom: theme.spacing(),
  },
  countdownTextHiddenLandscape: {
    marginTop: theme.spacing(),
    fontWeight: theme.typography.fontWeightRegular,
    fontSize: 'clamp(17px, 3.1vmin, 29px)',
    textAlign: 'center',
    color: 'transparent',
    marginBottom: theme.spacing(),
  },
  qr: {
    margin: theme.spacing(),
  },
  box: {
    position: 'absolute',
    top: 16,
    right: 16,
  },
  closeIcon: {
    color: theme.palette.common.black,
  },
  refresh: {
    height: 60,
    width: 60,
  },
  tile: {
    display: 'flex',
    justifyContent: 'center',
  },
  root: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    flexWrap: 'wrap',
    width: 350,
    height: '100%',
    marginTop: theme.spacing(),
    marginBottom: theme.spacing(8),
  },
  qrContainer: {
    margin: 'clamp(24px, 12vmin, 136px)',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    [theme.breakpoints.down('xs')]: {
      width: '90vw',
      height: 'auto',
      margin: '0 auto',
    },
  },
  refreshText: {
    color: theme.palette.grey[600],
    fontSize: 24,
    textAlign: 'center',
    zIndex: 2,
    cursor: 'pointer',
  },
  refreshIcon: {
    height: 200,
    width: 200,
    color: theme.palette.grey[600],
    marginBottom: theme.spacing(1),
    zIndex: 2,
    cursor: 'pointer',
  },
  mainSubtextContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 'clamp(8px, 3vmin, 32px)',
  },
  subtext: {
    fontWeight: theme.typography.fontWeightRegular,
    fontSize: 'clamp(20px, 6vmin, 48px)',
    textAlign: 'center',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    lineHeight: 1.2,
    paddingLeft: theme.spacing(7),
    paddingRight: theme.spacing(7),
    [theme.breakpoints.down('xs')]: {
      paddingLeft: theme.spacing(5),
      paddingRight: theme.spacing(5),
    },
  },
  formControl: {
    minWidth: 250,
  },
  selectEmpty: {
    marginTop: theme.spacing(2),
  },
  // Height-bounded so the column can never spill off the top/bottom of the
  // screen: this is a kiosk, `touchAction` is disabled, so anything pushed
  // outside the viewport is unreachable rather than merely off-screen. The QR
  // is fitted to the leftover space in JS (getFittedQrSize), which keeps this
  // from ever clipping in practice; `overflow: hidden` is the last-resort net.
  containerLandscape: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    maxHeight: '100vh',
    overflow: 'hidden',
    boxSizing: 'border-box',
    width: '100vw',
  },
  headerLandscape: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    lineHeight: 1.2,
    textAlign: 'center',
    flexShrink: 0,
    paddingLeft: theme.spacing(5),
    paddingRight: theme.spacing(5),
    [theme.breakpoints.down('xs')]: {
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(3),
    },
  },
  contentLandscape: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    // The QR row gives up space to the text blocks, never the other way round.
    minHeight: 0,
  },
  footerLandscape: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    wordBreak: 'break-word',
    textAlign: 'center',
    flexShrink: 0,
    paddingLeft: theme.spacing(5),
    paddingRight: theme.spacing(5),
    marginTop: theme.spacing(2),
  },
  logoContainerLandscape: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  // Landscape merchant logo. Its box is pinned to the QR size inline (so it
  // always matches the QR exactly); the clamp here is a safe fallback.
  merchantLogoLandscape: {
    height: 'clamp(120px, 40vmin, 400px)',
    width: 'clamp(120px, 40vmin, 400px)',
    borderRadius: '50%',
    objectFit: 'cover',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
    marginLeft: 'clamp(16px, 6vmin, 80px)',
  },
  subtextLandscape: {
    fontWeight: theme.typography.fontWeightRegular,
    // Capped by width as well as height - see `titleHeaderLandscape`. Both caps
    // move together, so the title keeps outranking this line everywhere:
    // 1.68-1.73x on tablets, 1.46x on phone landscape (where this hits its 24px
    // floor while the title is still on `9vmin`).
    fontSize: 'clamp(24px, min(5.2vmin, 3.1vw), 42px)',
    textAlign: 'center',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    lineHeight: 1.2,
    paddingLeft: theme.spacing(5),
    paddingRight: theme.spacing(5),
  },
  grow: {
    height: 16,
    width: 16,
  },
  animationContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    width: '100vw',
  },
  loadingText: {
    marginTop: theme.spacing(),
  },
  buttonContainer: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    width: 90,
    height: 90,
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'flex-start',
    paddingBottom: 'max(10px, env(safe-area-inset-bottom))',
    paddingLeft: 'max(10px, env(safe-area-inset-left))',
    background: 'transparent',
    boxSizing: 'border-box',
    zIndex: 10,
  },
  bottomMenuContainer: {
    width: 250,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  close: {
    width: 80,
    height: 80,
  },
  greyIcon: {
    color: theme.palette.grey[100],
  },
  noNetworkContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    width: '100vw',
  },
  wifiOff: {
    width: 150,
    height: 150,
  },
  noNetworkHeader: {
    marginBottom: theme.spacing(),
  },
  noNetworkSubText: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    fontWeight: theme.typography.fontWeightRegular,
    fontSize: 16,
    textAlign: 'center',
  },
  titleHeaderLandscape: {
    marginTop: theme.spacing(2),
    // Capped by *both* axes. `9vmin` alone (= height in landscape) is blind to
    // the width the title has to wrap into, so a 962x601 8" tablet and a
    // 1024x600 7" tablet got the same ~54px while only the wider one fit the
    // title on one line - the extra line then pushed the subtext off-screen.
    // `5.2vw` is the largest width-relative size that still holds one line at
    // 962 wide, and it never exceeds the old value, so it cannot itself cause
    // an overflow: at 1024x600 `min()` picks 53.2px (unchanged in practice) and
    // on phone landscape it picks `9vmin` exactly as before.
    fontSize: 'clamp(34px, min(9vmin, 5.2vw), 88px)',
  },
  versionText: {
    fontWeight: theme.typography.fontWeightLight,
    color: theme.palette.grey[100],
    fontSize: 18,
    cursor: 'pointer',
    position: 'fixed',
    top: 12,
    left: 12,
    zIndex: 10,
  },
}));

type Props = {
  onLogout: () => void;
  storeId: string;
  onlyScanOnce: boolean;
  storeName: string;
};

export default function KioskCodeOnlyScreen({
  onLogout,
  storeId,
  onlyScanOnce,
  storeName,
}: Props) {
  const isMountedRef = useRef(true);
  const regenFetchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const errorReloadTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isOnline = useNetworkStatus();
  const isCurrentlyOffline =
    isOnline === false ||
    (typeof isOnline !== 'boolean' &&
      typeof navigator !== 'undefined' &&
      navigator.onLine === false);
  const prevIsOnline = useRef(isOnline);
  const { orientation, tier, shortSide, height } = useResponsiveScreen();
  const isMobile = tier === 'mobile';
  const desiredQrSize = getQrSize(tier, shortSide, orientation);
  // The landscape title and subtitle are merchant-authored free text, so how
  // many lines they occupy - and therefore how much height is left for the QR -
  // can only be measured, not derived from the viewport. Measure them and let
  // the QR absorb the difference; where there is room to spare (the common
  // case) the fit returns `desiredQrSize` untouched.
  const [headerRef, headerHeight] = useMeasuredHeight<HTMLDivElement>();
  const [footerRef, footerHeight] = useMeasuredHeight<HTMLDivElement>();
  const qrSize =
    orientation === 'landscape'
      ? getFittedQrSize({
          desiredSize: desiredQrSize,
          viewportHeight: height,
          reservedHeight: headerHeight + footerHeight + LANDSCAPE_QR_GUTTER,
        })
      : desiredQrSize;
  const classes = useStyles();
  const { company } = useCompanyContext();

  const [deeplink, setDeeplink] = useState('');
  const [countdown, setCountdown] = useState(-1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [loyaltyProgram, setLoyaltyProgram] =
    useState<LoyaltyProgramService.LoyaltyProgram | null>(null);
  const [showMenuPIN, setShowMenuPIN] = useState(false);
  const [pinVerified, setPinVerified] = useState(false);
  const [logoLoadFailed, setLogoLoadFailed] = useState(false);

  // New states for error, fetch-in-progress, and loading
  const [fetchError, setFetchError] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [loading, setLoading] = useState(true);
  const logoSrc = company?.logo?.versions?.xl || '';
  const logoFallbackInitial = (storeName || 'Store').trim().charAt(0) || 'S';
  const shouldShowLogoImage = Boolean(logoSrc) && !logoLoadFailed;

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (regenFetchTimeoutRef.current) {
        clearTimeout(regenFetchTimeoutRef.current);
      }
      if (errorReloadTimerRef.current) {
        clearTimeout(errorReloadTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleKioskNetworkError = () => {
      if (!isMountedRef.current) return;
      setIsFetching(false);
      setLoading(false);
      setFetchError(true);
    };

    window.addEventListener(KIOSK_NETWORK_ERROR_EVENT, handleKioskNetworkError);

    return () => {
      window.removeEventListener(
        KIOSK_NETWORK_ERROR_EVENT,
        handleKioskNetworkError,
      );
    };
  }, []);

  useEffect(() => {
    setLogoLoadFailed(false);
  }, [logoSrc]);

  // Centralized QR code fetch logic
  const fetchQRCode = useCallback(async () => {
    if (!company || isFetching || !isMountedRef.current) return;
    setIsFetching(true);
    setLoading(true);
    try {
      const loyaltyRes = await LoyaltyProgramService.get(company.id);
      if (!isMountedRef.current) return;
      setLoyaltyProgram(loyaltyRes);
      const resStampCampaign = await StampCampaignService.get(
        company.id,
        loyaltyRes.stampCampaign.id,
      );

      const res = await KioskCodeService.generate({
        companyId: company.id,
        slotNumber: resStampCampaign.slotNumber,
        storeId,
      });
      if (!isMountedRef.current) return;
      setDeeplink(res.deepLink);
      setCountdown(loyaltyRes.qrRegenCodeInterval * 60);
      setFetchError(false);
    } catch (e) {
      if (isMountedRef.current) {
        setFetchError(true);
      }
    } finally {
      if (isMountedRef.current) {
        setIsFetching(false);
        setLoading(false);
      }
    }
  }, [company, storeId, isFetching]);

  // Initial fetch when company becomes available
  useEffect(() => {
    if (company) {
      setLoading(true);
      fetchQRCode();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company]);

  // Automatic retry on fetch error (5s delay)
  useEffect(() => {
    if (fetchError) {
      const retryTimer = setTimeout(() => {
        fetchQRCode();
      }, 5000);
      return () => clearTimeout(retryTimer);
    }
  }, [fetchError, fetchQRCode]);

  // Loading timeout: only start the 15s clock once the fetch is actually
  // in-flight (isFetching = true). This avoids a false network error caused
  // by the company context taking time to load before the fetch even begins.
  useEffect(() => {
    if (!isFetching) return;
    const loadingTimeout = setTimeout(() => {
      if (isMountedRef.current) {
        setIsFetching(false);
        setLoading(false);
        setFetchError(true);
      }
    }, 15000);
    return () => clearTimeout(loadingTimeout);
  }, [isFetching]);

  // Auto-refresh kiosk page to keep session state fresh.
  // Normal: reload every 60 minutes.
  // Error/offline: reload after 2 minutes to recover automatically.
  useEffect(() => {
    const timer = setInterval(
      () => {
        window.location.reload();
      },
      60 * 60 * 1000,
    );
    return () => clearInterval(timer);
  }, []);

  // When the kiosk first enters error/offline state, schedule a one-shot
  // full reload after 2 minutes. Uses a ref so repeated fetchError toggles
  // (caused by 5s retry cycles) do not cancel and restart the timer.
  useEffect(() => {
    const inErrorState = fetchError || isCurrentlyOffline;
    if (inErrorState) {
      if (!errorReloadTimerRef.current) {
        errorReloadTimerRef.current = setTimeout(
          () => {
            window.location.reload();
          },
          2 * 60 * 1000,
        );
      }
    } else {
      // Recovered — cancel any pending reload only after we have data again.
      const hasRecoveredData = Boolean(deeplink);
      if (!hasRecoveredData) {
        return;
      }
      if (errorReloadTimerRef.current) {
        clearTimeout(errorReloadTimerRef.current);
        errorReloadTimerRef.current = null;
      }
    }
  }, [fetchError, isCurrentlyOffline, deeplink]);

  useEffect(() => {
    if (!isOnline) {
      setDeeplink('');
      setCountdown(-1);
      setLoading(false);
    }
    if (!prevIsOnline.current && isOnline) {
      setLoading(true);
      fetchQRCode();
    }
    prevIsOnline.current = isOnline;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnline]);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (isPlaying) {
      timer = setTimeout(() => {
        setIsPlaying(false);
      }, 1500);
    }
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [isPlaying]);

  // Disable browser zoom gestures while kiosk screen is active.
  useEffect(() => {
    if (pinVerified) {
      return;
    }

    const originalTouchAction = document.body.style.touchAction;
    document.body.style.touchAction = 'none';

    const preventGesture = (event: Event) => {
      event.preventDefault();
    };

    const preventMultiTouch = (event: TouchEvent) => {
      if (event.touches.length > 1) {
        event.preventDefault();
      }
    };

    const preventCtrlWheelZoom = (event: WheelEvent) => {
      if (event.ctrlKey) {
        event.preventDefault();
      }
    };

    let lastTouchEnd = 0;
    const preventDoubleTapZoom = (event: TouchEvent) => {
      const now = Date.now();
      if (now - lastTouchEnd <= 300) {
        event.preventDefault();
      }
      lastTouchEnd = now;
    };

    const nonPassiveOptions = { passive: false } as any;

    document.addEventListener(
      'gesturestart' as any,
      preventGesture as EventListener,
      nonPassiveOptions,
    );
    document.addEventListener(
      'gesturechange' as any,
      preventGesture as EventListener,
      nonPassiveOptions,
    );
    document.addEventListener(
      'gestureend' as any,
      preventGesture as EventListener,
      nonPassiveOptions,
    );
    document.addEventListener(
      'touchmove',
      preventMultiTouch as EventListener,
      nonPassiveOptions,
    );
    document.addEventListener(
      'touchend',
      preventDoubleTapZoom as EventListener,
      nonPassiveOptions,
    );
    document.addEventListener(
      'wheel',
      preventCtrlWheelZoom as EventListener,
      nonPassiveOptions,
    );

    return () => {
      document.body.style.touchAction = originalTouchAction;
      document.removeEventListener(
        'gesturestart' as any,
        preventGesture as EventListener,
        nonPassiveOptions,
      );
      document.removeEventListener(
        'gesturechange' as any,
        preventGesture as EventListener,
        nonPassiveOptions,
      );
      document.removeEventListener(
        'gestureend' as any,
        preventGesture as EventListener,
        nonPassiveOptions,
      );
      document.removeEventListener(
        'touchmove',
        preventMultiTouch as EventListener,
        nonPassiveOptions,
      );
      document.removeEventListener(
        'touchend',
        preventDoubleTapZoom as EventListener,
        nonPassiveOptions,
      );
      document.removeEventListener(
        'wheel',
        preventCtrlWheelZoom as EventListener,
        nonPassiveOptions,
      );
    };
  }, [pinVerified]);

  // Countdown timer and periodic QR code regeneration
  useEffect(() => {
    const countdownTimer = setTimeout(() => {
      if (countdown > 0) {
        setCountdown(countdown - 1);
      } else if (countdown === 0 && !isFetching && !fetchError) {
        setIsPlaying(true);
        if (regenFetchTimeoutRef.current) {
          clearTimeout(regenFetchTimeoutRef.current);
        }
        regenFetchTimeoutRef.current = setTimeout(() => {
          if (isMountedRef.current) {
            fetchQRCode();
          }
        }, 1500);
      }
    }, 1000);
    return () => {
      clearTimeout(countdownTimer);
    };
  }, [countdown, fetchQRCode, isFetching, fetchError]);

  const checkIfOnline = () => {
    if (isOnline) {
      window.location.reload();
    }
  };

  if (isCurrentlyOffline) {
    return (
      <div className={classes.noNetworkContainer}>
        <Typography
          variant={isMobile ? 'h5' : 'h3'}
          className={classes.noNetworkHeader}
        >
          No Internet Connection
        </Typography>
        <WifiOffIcon color="disabled" className={classes.wifiOff} />
        <Typography className={classes.noNetworkSubText}>
          Please check your connection and try again.
        </Typography>
        <Button variant="contained" color="primary" onClick={checkIfOnline}>
          REFRESH
        </Button>
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className={classes.noNetworkContainer}>
        <Typography
          variant={isMobile ? 'h5' : 'h3'}
          className={classes.noNetworkHeader}
        >
          Network Error
        </Typography>
        <WifiOffIcon color="disabled" className={classes.wifiOff} />
        <Typography className={classes.noNetworkSubText}>
          Please check your connection and tap REFRESH to try again.
        </Typography>
        <Button variant="contained" color="primary" onClick={checkIfOnline}>
          REFRESH
        </Button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={classes.animationContainer}>
        <Lottie
          animationData={cogs}
          loop={true}
          autoplay={true}
          style={{
            width: 'clamp(220px, 60vmin, 450px)',
            height: 'clamp(260px, 66vmin, 500px)',
            alignItems: 'center',
            justifyContent: 'center',
            marginLeft: 'auto',
            marginRight: 'auto',
          }}
        />
        <Typography
          variant={isMobile ? 'h6' : 'h4'}
          align="center"
          className={classes.loadingText}
        >
          Loading QR code...
        </Typography>
      </div>
    );
  }

  // Final safeguard: fallback UI for any unexpected blank state
  if (!company) {
    return (
      <div className={classes.animationContainer}>
        <Lottie
          animationData={cogs}
          loop={true}
          autoplay={true}
          style={{
            width: 'clamp(220px, 60vmin, 450px)',
            height: 'clamp(260px, 66vmin, 500px)',
            alignItems: 'center',
            justifyContent: 'center',
            marginLeft: 'auto',
            marginRight: 'auto',
          }}
        />
        <Typography variant="h5" align="center" color="error">
          Unable to load kiosk screen. Please try refreshing or contact support.
        </Typography>
      </div>
    );
  }

  return (
    <>
      {company ? (
        <>
          {isPlaying && (
            <div className={classes.animationContainer}>
              <Lottie
                animationData={cogs}
                loop={isPlaying}
                autoplay={false}
                style={{
                  width: 'clamp(220px, 60vmin, 450px)',
                  height: 'clamp(260px, 66vmin, 500px)',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginLeft: 'auto',
                  marginRight: 'auto',
                }}
              />
              <Typography
                variant={isMobile ? 'h6' : 'h4'}
                align="center"
                className={classes.loadingText}
              >
                Generating new code...
              </Typography>
            </div>
          )}
          {orientation === 'portrait' ? (
            <div>
              <>
                <Typography
                  className={classes.versionText}
                  onClick={() => window.location.reload()}
                >
                  v{version}
                </Typography>
              </>
              <div className={classes.logoContainer}>
                {shouldShowLogoImage ? (
                  <img
                    src={logoSrc}
                    className={classes.merchantLogo}
                    alt="flex reward"
                    onError={() => setLogoLoadFailed(true)}
                  />
                ) : (
                  <div
                    className={`${classes.merchantLogo} ${classes.logoFallback}`}
                    aria-label="merchant logo placeholder"
                  >
                    {logoFallbackInitial}
                  </div>
                )}
                {loyaltyProgram && (
                  <>
                    <Typography
                      variant={isMobile ? 'h4' : 'h2'}
                      className={classes.header}
                    >
                      {loyaltyProgram.qrMainTitle}
                    </Typography>
                    {countdown <= 30 && countdown >= 0 ? (
                      <Typography className={classes.countdownText}>
                        {countdown.toString()} second{countdown > 1 ? 's' : ''}{' '}
                        remaining ...
                      </Typography>
                    ) : (
                      <Typography className={classes.countdownTextHidden}>
                        {countdown.toString()} second{countdown > 1 ? 's' : ''}{' '}
                        remaining ...
                      </Typography>
                    )}
                    <div className={classes.qr}>
                      {countdown <= 0 ? (
                        // Same box as the live QR (in flow, no wrapper) so the
                        // layout doesn't shift while the next code is fetched.
                        <QRCode
                          value={deeplink}
                          size={qrSize}
                          renderAs="canvas"
                          includeMargin
                          fgColor={'#e1e1e1'}
                        />
                      ) : (
                        <QRCode
                          value={deeplink}
                          size={qrSize}
                          renderAs="svg"
                          includeMargin
                        />
                      )}
                    </div>
                    {isOnline && onlyScanOnce && (
                      <div className={classes.mainSubtextContainer}>
                        <Typography className={classes.subtext}>
                          {loyaltyProgram.qrSubTitle
                            ? loyaltyProgram.qrSubTitle
                            : storeName}
                        </Typography>
                      </div>
                    )}
                  </>
                )}
              </div>
              <div
                className={classes.buttonContainer}
                onClick={() => {
                  if (!showMenuPIN) {
                    setShowMenuPIN(true);
                  }
                  if (pinVerified) {
                    setDeeplink('');
                    onLogout();
                  }
                }}
              >
                <IconButton
                  onClick={() => {
                    if (!showMenuPIN) {
                      setShowMenuPIN(true);
                    }
                    if (pinVerified) {
                      setDeeplink('');
                      onLogout();
                    }
                  }}
                >
                  {!showMenuPIN && !pinVerified && (
                    <LockOutlinedIcon className={classes.greyIcon} />
                  )}
                  {pinVerified && (
                    <ExitToAppIcon className={classes.greyIcon} />
                  )}
                </IconButton>
              </div>
            </div>
          ) : (
            <div className={classes.containerLandscape}>
              <>
                <Typography
                  className={classes.versionText}
                  onClick={() => window.location.reload()}
                >
                  v{version}
                </Typography>
              </>
              {loyaltyProgram && (
                <div className={classes.headerLandscape} ref={headerRef}>
                  <Typography
                    variant={isMobile ? 'h4' : 'h1'}
                    className={classes.titleHeaderLandscape}
                  >
                    {loyaltyProgram.qrMainTitle}
                  </Typography>
                  {countdown <= 30 && countdown >= 0 ? (
                    <Typography className={classes.countdownTextLandscape}>
                      {countdown.toString()} second{countdown > 1 ? 's' : ''}{' '}
                      remaining ...
                    </Typography>
                  ) : (
                    <Typography
                      className={classes.countdownTextHiddenLandscape}
                    >
                      {countdown.toString()} second{countdown > 1 ? 's' : ''}{' '}
                      remaining ...
                    </Typography>
                  )}
                </div>
              )}
              <div className={classes.contentLandscape}>
                <div className={classes.qr}>
                  {countdown <= 0 ? (
                    <QRCode
                      value={deeplink}
                      size={qrSize}
                      renderAs="canvas"
                      fgColor={'#e1e1e1'}
                    />
                  ) : (
                    <QRCode value={deeplink} size={qrSize} renderAs="svg" />
                  )}
                </div>
                <div className={classes.logoContainerLandscape}>
                  {shouldShowLogoImage ? (
                    <img
                      src={logoSrc}
                      className={classes.merchantLogoLandscape}
                      style={{ width: qrSize, height: qrSize }}
                      alt="flex reward"
                      onError={() => setLogoLoadFailed(true)}
                    />
                  ) : (
                    <div
                      className={`${classes.merchantLogoLandscape} ${classes.logoFallback}`}
                      style={{ width: qrSize, height: qrSize }}
                      aria-label="merchant logo placeholder"
                    >
                      {logoFallbackInitial}
                    </div>
                  )}
                </div>
              </div>
              <div className={classes.footerLandscape} ref={footerRef}>
                <Typography className={classes.subtextLandscape}>
                  {loyaltyProgram && loyaltyProgram.qrSubTitle
                    ? loyaltyProgram.qrSubTitle
                    : storeName}
                </Typography>
              </div>
            </div>
          )}

          {loyaltyProgram && (
            <>
              <div className={classes.bottomMenuContainer}>
                <div
                  className={classes.buttonContainer}
                  onClick={() => {
                    if (!showMenuPIN) {
                      setShowMenuPIN(true);
                    }
                    if (pinVerified) {
                      setDeeplink('');
                      onLogout();
                    }
                  }}
                >
                  <IconButton
                    onClick={() => {
                      if (!showMenuPIN) {
                        setShowMenuPIN(true);
                      }
                      if (pinVerified) {
                        setDeeplink('');
                        onLogout();
                      }
                    }}
                  >
                    {!showMenuPIN && !pinVerified && (
                      <LockOutlinedIcon className={classes.greyIcon} />
                    )}
                    {pinVerified && (
                      <ExitToAppIcon className={classes.greyIcon} />
                    )}
                  </IconButton>
                </div>
              </div>
              <UnlockPINDialog
                open={showMenuPIN}
                onClose={() => setShowMenuPIN(false)}
                onSuccess={() => {
                  setPinVerified(true);
                  setShowMenuPIN(false);
                }}
                menuPIN={loyaltyProgram.menuUnlockPin}
              />
            </>
          )}
        </>
      ) : (
        <div className={classes.noNetworkContainer}>
          <Typography
            variant={isMobile ? 'h5' : 'h3'}
            className={classes.noNetworkHeader}
          >
            Network Error
          </Typography>
          <WifiOffIcon color="disabled" className={classes.wifiOff} />
          <Typography className={classes.noNetworkSubText}>
            Please check your connection and tap REFRESH to try again.
          </Typography>
          <Button variant="contained" color="primary" onClick={checkIfOnline}>
            REFRESH
          </Button>
        </div>
      )}
    </>
  );
}
